import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, dateFmt, API_BASE } from "@/lib/api";
import { StatusBadge, UrgencyBadge, UnitStatusBadge } from "@/components/StatusBadge";
import { ArrowLeft, MapPin, Activity, Truck, User, Download } from "lucide-react";
import ServiceMap from "@/components/ServiceMap";
import ServiceEvidences from "@/components/ServiceEvidences";

export default function PortalServiceDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await api.get(`/portal/services/${id}`);
                if (r?.data) setData(r.data);
            } catch (e) { console.warn(e); }
        };
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [id]);

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando...</div>;
    const { service, unit } = data;

    return (
        <div className="space-y-4" data-testid="portal-service-detail">
            <Link to="/portal/services" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Mis servicios
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <div className="fm-card p-5">
                        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                            <div>
                                <div className="font-mono text-xs text-slate-500">{service.folio}</div>
                                <h2 className="font-display font-black text-2xl tracking-tight">{service.service_type_name}</h2>
                                <div className="flex items-center gap-2 mt-2">
                                    <StatusBadge status={service.status} />
                                    <UrgencyBadge urgency={service.urgency_level} />
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total</div>
                                <div className="font-mono font-bold text-2xl tabular-nums">{money(service.total)}</div>
                                <div className="text-[10px] text-slate-500">IVA incluido</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-slate-200">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Paciente</div>
                                <div className="text-sm font-medium">{service.patient_name}</div>
                                <div className="text-xs text-slate-500">{service.patient_age ? `${service.patient_age} años` : ""} {service.patient_sex}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Motivo</div>
                                <div className="text-sm">{service.reason || "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Origen</div>
                                <div className="text-sm">{service.origin_address}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Destino</div>
                                <div className="text-sm">{service.destination_address || "—"}</div>
                            </div>
                        </div>
                    </div>

                    {/* Live tracking map - polilínea GPS */}
                    <ServiceMap service={service} unit={unit} height={340} />

                    {/* Evidencias (solo lectura) */}
                    {(service.evidences || []).length > 0 && (
                        <ServiceEvidences service={service} readOnly />
                    )}

                    {/* Status timeline */}
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                            <Activity className="w-4 h-4" strokeWidth={1.5} /> Historial del servicio
                        </h3>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {(service.status_history || []).slice().reverse().map((h, i) => (
                                <div key={i} className="flex gap-3 pb-2 border-b border-slate-100 last:border-0">
                                    <div className="w-2 h-2 rounded-full bg-slate-900 mt-1.5 flex-shrink-0"></div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <StatusBadge status={h.status} />
                                            <span className="text-[10px] font-mono text-slate-500">{dateFmt(h.at)}</span>
                                        </div>
                                        {h.comment && <div className="text-xs text-slate-700 mt-0.5">"{h.comment}"</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                    {unit ? (
                        <div className="fm-card p-5">
                            <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                                <Truck className="w-4 h-4" strokeWidth={1.5} /> Unidad asignada
                            </h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-slate-500">Económico</span><span className="font-mono font-bold">{unit.economic_number}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Vehículo</span><span>{unit.brand} {unit.model}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Tipo</span><span className="capitalize">{unit.ambulance_type}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Placas</span><span className="font-mono">{unit.plates}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Estatus</span><UnitStatusBadge status={unit.status} /></div>
                            </div>
                        </div>
                    ) : (
                        <div className="fm-card p-5">
                            <h3 className="font-display font-bold text-base mb-2">Unidad asignada</h3>
                            <div className="text-sm text-slate-500">Cabina está procesando tu solicitud. Cuando se asigne unidad, podrás verla aquí con su ubicación en tiempo real.</div>
                        </div>
                    )}

                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-3">Resumen económico</h3>
                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono tabular-nums">{money(service.subtotal || service.base_price)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">IVA</span><span className="font-mono tabular-nums">{money(service.iva || service.base_price * 0.16)}</span></div>
                            <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                                <span className="font-display font-bold">Total</span>
                                <span className="font-mono font-bold tabular-nums">{money(service.total)}</span>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                            <span className="text-xs text-slate-500">Estatus de pago</span>
                            {service.paid ? <span className="fm-badge fm-badge-positive">Pagado</span> :
                                service.invoiced ? <span className="fm-badge fm-badge-warning">Facturado</span> :
                                <span className="fm-badge fm-badge-neutral">Pendiente</span>}
                        </div>
                    </div>
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-3">Documentos</h3>
                        <div className="space-y-2">
                            <a href={`${API_BASE}/pdf/service/${service.id}`} target="_blank" rel="noreferrer" className="fm-btn-outline w-full justify-center" data-testid="portal-pdf-service">
                                <Download className="w-3.5 h-3.5" /> Reporte de servicio PDF
                            </a>
                            {service.invoice_id && (
                                <a href={`${API_BASE}/pdf/invoice/${service.invoice_id}`} target="_blank" rel="noreferrer" className="fm-btn-outline w-full justify-center">
                                    <Download className="w-3.5 h-3.5" /> Factura PDF
                                </a>
                            )}
                            {service.frap_id && (
                                <a href={`${API_BASE}/pdf/frap/${service.id}`} target="_blank" rel="noreferrer" className="fm-btn-outline w-full justify-center">
                                    <Download className="w-3.5 h-3.5" /> Reporte FRAP PDF
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
